import React from "react";
import Icon from "components/AppIcon";

const Footer = ({ language }) => {
  const translations = {
    en: {
      quickLinks: "Quick Links",
      services: "Services",
      contact: "Contact Info",
      followUs: "Follow Us",
      newsletter: "Newsletter",
      newsletterText:
        "Subscribe to get updates on classes, events, and fitness tips",
      subscribe: "Subscribe",
      email: "Enter your email",
      links: {
        home: "Home",
        about: "About Us",
        membership: "Membership",
        classes: "Classes",
        trainers: "Trainers",
        contact: "Contact",
      },
      servicesList: {
        personal: "Personal Training",
        group: "Group Classes",
        nutrition: "Nutrition Counseling",
        recovery: "Recovery Services",
      },
      address: "Aljamaa Street",
      phone: "+1 (555) 123-4567",
      emailContact: "info@fitprocenter.com",
      hours: "Mon-Fri: 5AM-11PM | Sat-Sun: 6AM-10PM",
      copyright: "© 2024 FitPro Center. All rights reserved.",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
    },
    ar: {
      quickLinks: "روابط سريعة",
      services: "الخدمات",
      contact: "معلومات الاتصال",
      followUs: "تابعنا",
      newsletter: "النشرة الإخبارية",
      newsletterText:
        "اشترك للحصول على تحديثات حول الفصول والأحداث ونصائح اللياقة البدنية",
      subscribe: "اشترك",
      email: "أدخل بريدك الإلكتروني",
      links: {
        home: "الرئيسية",
        about: "حولنا",
        membership: "العضوية",
        classes: "الفصول",
        trainers: "المدربون",
        contact: "اتصل بنا",
      },
      servicesList: {
        personal: "التدريب الشخصي",
        group: "الفصول الجماعية",
        nutrition: "استشارة التغذية",
        recovery: "خدمات الاستشفاء",
      },
      address: "حي الجامعة",
      phone: "+1 (555) 123-4567",
      emailContact: "info@fitprocenter.com",
      hours: "الإثنين-الجمعة: 5ص-11م | السبت-الأحد: 6ص-10م",
      copyright: "© 2024 مركز فيت برو. جميع الحقوق محفوظة.",
      privacy: "سياسة الخصوصية",
      terms: "شروط الخدمة",
    },
  };

  const t = translations[language];
  const isRTL = language === "ar";

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    // Handle newsletter subscription
    const formData = new FormData(e.target);
    const email = formData.get("email");
    console.log("Newsletter subscription:", email);
    e.target.reset();
  };

  return (
    <footer className={`bg-gray-900 text-white ${isRTL ? "rtl" : "ltr"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                <Icon name="Dumbbell" size={24} color="white" />
              </div>
              <span className="text-2xl font-bold">FitPro Center</span>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              {language === "ar"
                ? "حوّل جسمك، حوّل حياتك. انضم إلى مجتمعنا من عشاق اللياقة البدنية وحقق أهدافك الصحية مع الإرشاد الخبير."
                : "Transform your body, transform your life. Join our community of fitness enthusiasts and achieve your health goals with expert guidance."}
            </p>

            {/* Social Media */}
            <div>
              <h4 className="font-semibold mb-4">{t.followUs}</h4>
              <div className="flex gap-3">
                {[
                  { name: "Facebook", icon: "Facebook" },
                  { name: "Instagram", icon: "Instagram" },
                  { name: "Twitter", icon: "Twitter" },
                  { name: "YouTube", icon: "Youtube" },
                ].map((social, index) => (
                  <button
                    key={index}
                    className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors"
                    aria-label={social.name}
                  >
                    <Icon name={social.icon} size={16} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-6">{t.quickLinks}</h4>
            <div className="space-y-3">
              {Object.entries(t.links).map(([key, value]) => (
                <button
                  key={key}
                  onClick={() =>
                    scrollToSection(
                      key === "home"
                        ? "hero"
                        : key === "membership"
                        ? "plans"
                        : key
                    )
                  }
                  className="block text-gray-400 hover:text-green-400 transition-colors"
                >
                  {value}
                </button>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold mb-6">{t.services}</h4>
            <div className="space-y-3">
              {Object.values(t.servicesList).map((service, index) => (
                <div key={index} className="text-gray-400">
                  {service}
                </div>
              ))}
            </div>
          </div>

          {/* Contact & Newsletter */}
          <div>
            <h4 className="font-semibold mb-6">{t.contact}</h4>
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3 text-gray-400">
                <Icon
                  name="MapPin"
                  size={16}
                  className="text-green-400 mt-1 flex-shrink-0"
                />
                <span className="text-sm">{t.address}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-400">
                <Icon
                  name="Phone"
                  size={16}
                  className="text-green-400 flex-shrink-0"
                />
                <span className="text-sm">{t.phone}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-400">
                <Icon
                  name="Mail"
                  size={16}
                  className="text-green-400 flex-shrink-0"
                />
                <span className="text-sm">{t.emailContact}</span>
              </div>
              <div className="flex items-center gap-3 text-gray-400">
                <Icon
                  name="Clock"
                  size={16}
                  className="text-green-400 flex-shrink-0"
                />
                <span className="text-sm">{t.hours}</span>
              </div>
            </div>

            {/* Newsletter */}
            <div>
              <h4 className="font-semibold mb-3">{t.newsletter}</h4>
              <p className="text-gray-400 text-sm mb-4">{t.newsletterText}</p>
              <form onSubmit={handleNewsletterSubmit} className="space-y-3">
                <input
                  type="email"
                  name="email"
                  placeholder={t.email}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-white placeholder-gray-400"
                  required
                />
                <button
                  type="submit"
                  className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  {t.subscribe}
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">{t.copyright}</p>
            <div className="flex gap-6">
              <button className="text-gray-400 hover:text-green-400 text-sm transition-colors">
                {t.privacy}
              </button>
              <button className="text-gray-400 hover:text-green-400 text-sm transition-colors">
                {t.terms}
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
